import { ChainID } from '@ankr.com/chains-list';
import { useMemo } from 'react';
import { ETelemetryTopOf } from 'multirpc-sdk';

import { IUsePrivateStatsProps } from 'modules/stats/hooks/usePrivateStats';
import { StatsTimeframe } from 'modules/stats/types';
import { selectJWTsState } from 'domains/jwtToken/action/fetchJWTs';
import { selectUserEndpointTokens } from 'domains/jwtToken/store/selectors';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { mapChainIdToPath } from 'modules/chains/utils/mapChainIdToPath';

export interface IUseProjectsStatsParamsProps {
  blockchains?: string[];
  skipFetching?: boolean;
  timeframe: StatsTimeframe;
  timeframeShift: number;
}

const metrics = [ETelemetryTopOf.API_KEY];

export const useProjectsStatsParams = ({
  blockchains,
  skipFetching: skipFetchingProp,
  timeframe,
  timeframeShift,
}: IUseProjectsStatsParamsProps) => {
  const { group, isAccountInitialized, workerToken } = useAccount();

  const { isSuccess: projectsLoaded } = useAppSelector(state =>
    selectJWTsState(state, { group }),
  );

  const tokens = useAppSelector(state =>
    selectUserEndpointTokens(state, { group }),
  );

  const hasTokens = tokens.length > 0;

  const skipFetching =
    skipFetchingProp || !hasTokens || !projectsLoaded || !isAccountInitialized;

  const projectsStatsParams = useMemo(
    (): IUsePrivateStatsProps => ({
      blockchains: blockchains?.map(chain =>
        mapChainIdToPath(chain as ChainID),
      ),
      metrics,
      skipFetching,
      timeframe,
      timeframeShift,
      tokens,
      workerToken: workerToken!,
    }),
    [blockchains, skipFetching, timeframe, timeframeShift, tokens, workerToken],
  );

  return projectsStatsParams;
};
